import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import React, { FC, memo, useState } from 'react'

import { CheckRounded } from '@Assets/SVG'
import SvgAddToCart2 from '@Assets/SVG/AddToCart2'
import { Chip } from '@Components/Chips/Chip/Chip'
import { ImageV3, ImageV4 } from '@Components/Image'
import { Link } from '@Components/Link'
import { WishlistButton } from '@Components/WishList/WishlistButton'
import { ProductTileChip } from '@Domains/Product/Tiles/types'
import { useWishList } from '@Domains/WishList/Hooks'
import { useTranslation } from '@Globals/Translations'
import { Spinner } from '@Shared/Components/Spinner/Spinner'

import styles from './ProductTilesV2.module.scss'

interface ProductTileV2Props {
  alreadyAddedToCart?: boolean
  boosted?: boolean
  bottomChips?: ProductTileChip[]
  brand?: string
  bundleCombinedValue?: string
  className?: string
  disableBuyButton?: boolean
  disableWishButton?: boolean
  enableHover?: boolean
  hoverImage?: string
  imageWidth?: string
  intersecting?: boolean
  isOutOfStock?: boolean
  isWishedFor?: boolean
  loadingBuyButton?: boolean
  onBuy?: (id: string) => void
  originalPrice: string
  overwrittenPrice?: string
  primaryImage?: string
  priority?: boolean
  productID: string
  sourceID?: string
  title: string
  topChips?: ProductTileChip[]
  url: string
  variants?: number
  variantsTitle?: string
}

export const ProductTileV2: FC<ProductTileV2Props> = memo(({
  alreadyAddedToCart,
  enableHover = true,
  boosted = false,
  bottomChips,
  brand,
  bundleCombinedValue,
  className,
  productID,
  primaryImage,
  isOutOfStock = false,
  hoverImage,
  loadingBuyButton,
  imageWidth,
  disableBuyButton = loadingBuyButton,
  disableWishButton = false,
  onBuy,
  originalPrice,
  priority = true,
  intersecting = true,
  overwrittenPrice,
  title,
  topChips,
  url,
  variants,
  variantsTitle = '',
  sourceID,
}) => {
  const { t } = useTranslation()
  const [loaded, setLoaded] = useState(false)
  const [isWishedFor, toggleWishedFor] = useWishList(productID, sourceID)
  const contentVisibility = intersecting || priority
  return (
    <Link
      analytics={{
        source_id: sourceID,
        dest_page_type: 'product',
        dest_page_type_id: productID,
      }}
      className={clsx(
        'th-p',
        className,
        styles.ProductTileV2,
        !!onBuy && styles.withActions,
        contentVisibility && styles.priority,
        !!hoverImage && enableHover && styles.hasHover,
        boosted && 'th-b',
      )}
      data-intersection-id={productID}
      href={url}
      id={productID}
      onMouseOver={() => setLoaded(true)}
    >
      {isOutOfStock && <div className={styles.outOfStock}><p>{t('cms:OUT_OF_STOCK')}</p></div>}
      {!priority && intersecting && primaryImage && (
        <ImageV4
          alt={title}
          className={clsx(styles.images, styles.image)}
          decoding="async"
          draggable={false}
          fetchPriority="auto"
          loading="eager"
          onLoad={() => setLoaded(true)}
          src={primaryImage}
          style={{
            objectFit: 'contain',
          }}
          title={title}
          width={imageWidth || (priority || enableHover) ? 400 : 180}
        />
      )}
      {priority && primaryImage && (
        <ImageV3
          alt={title}
          className={clsx(styles.images, styles.image)}
          decoding="async"
          draggable={false}
          fetchPriority="high"
          fill
          loading="eager"
          onLoad={() => setLoaded(true)}
          sizes="(max-width: 1024px) 180px, 400px"
          src={primaryImage}
          style={{
            objectFit: 'contain',
          }}
          title={title}
        />
      )}
      {contentVisibility && hoverImage && loaded && enableHover && (
        <ImageV4
          alt={`hover: ${title}`}
          className={clsx(styles.images, styles.imageHover)}
          draggable={false}
          src={hoverImage}
          style={{
            objectFit: 'cover',
          }}
          title={`hover: ${title}`}
          width={imageWidth || enableHover ? 500 : 180}
        />
      )}
      <p className={styles.title}>{title}</p>
      {!isEmpty(topChips) && (
        <div className={styles.topChips}>
          {map(topChips, chip => (
            <Chip key={`chip${'text' in chip ? chip.text : chip.translation}`} color={chip.color} icon={chip.icon}>
              {'text' in chip ? chip.text : t(...(chip.translation as unknown as any))}
            </Chip>
          ))}
        </div>
      )}
      {!isEmpty(bottomChips) && (
        <div className={styles.bottomChips}>
          {map(bottomChips, chip => (
            <Chip key={`chip${'text' in chip ? chip.text : chip.translation}`} color={chip.color} icon={chip.icon}>
              {'text' in chip ? chip.text : t(...(chip.translation as unknown as any))}
            </Chip>
          ))}
        </div>
      )}
      <div className={styles.price}>
        <p className={clsx(overwrittenPrice && styles.strikeThrough)}>{originalPrice}</p>
        <p>{overwrittenPrice}</p>
      </div>
      {bundleCombinedValue && (
        <div className={styles.bundle}>
          {`${t('common:BUNDLE_COMBINED_VALUE')} - ${bundleCombinedValue}`}
        </div>
      )}
      {!!onBuy && (
        <div
          className={styles.actions}
        >
          <button
            className={clsx(styles.buyButton, alreadyAddedToCart && styles.success, sourceID)}
            disabled={disableBuyButton}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onBuy(productID)
            }}
            type="button"
          >
            {' '}
            {loadingBuyButton && <Spinner className={styles.addToCartSpinner} />}
            {(!loadingBuyButton && !alreadyAddedToCart) && <SvgAddToCart2 />}
            {(!loadingBuyButton && alreadyAddedToCart) && <CheckRounded className={styles.alreadyAddedIcon} />}
          </button>
        </div>
      )}
      {!disableWishButton && (
        <WishlistButton
          className={styles.wish}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            toggleWishedFor()
          }}
          selected={isWishedFor}
        />
      )}

      {!!(variants || brand) && (
        <div className={styles.brandVariants}>
          {!!variants && (
            <span>
              <p className={styles.variants}>{`${variants} ${variantsTitle}`}</p>
            </span>
          )}
          {!!brand && <p className={styles.brand}>{brand}</p>}
        </div>
      )}

    </Link>
  )
}, (prev, next) => prev.className === next.className
    && prev.intersecting === next.intersecting
    && prev.enableHover === next.enableHover
    && prev.loadingBuyButton === next.loadingBuyButton
    && prev.disableBuyButton === next.disableBuyButton
    && prev.isOutOfStock === next.isOutOfStock
    && prev.onBuy === next.onBuy)
